<template>
  <div class="visits-overview-list">
    <div v-if="visits.length == 0 && !loading" class="no-results">
      <span>No results found</span>
    </div>
    <ListView
      class="visit-list"
      :data-items="visits"
      item="visitTile"
      footer="visitListFooter"
      @scroll="scrollHandler($event as unknown as Event)"
      data-cy="visit-list"
    >
      <template v-slot:visitTile="{ props }">
        <VisitsOverviewTile
          class="visit-list__tile"
          :visit="props.dataItem"
          :selected="props.dataItem === selectedVisit"
          @visitClicked="onVisitClicked"
          @visitSelected="onVisitSelected"
          @groupPlantClicked="onGroupPlantClicked"
        />
      </template>

      <template v-slot:visitListFooter>
        <Loader
          v-show="loading"
          class="visit-loader"
          size="large"
          type="pulsing"
          data-cy="visit-loader"
        />
      </template>
    </ListView>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect } from "vue";
import { ListView } from "@progress/kendo-vue-listview";
import { Loader } from "@progress/kendo-vue-indicators";
import VisitsOverviewTile from "./VisitsOverviewTile.vue";
import type { VisitDisplay } from "@/Domain/VisitDisplay";

const props = defineProps<{
  visits: VisitDisplay[];
  selectedVisit?: VisitDisplay;
  totalVisits: number;
  defaultTake?: number;
  skip: number;
  loading: boolean;
}>();

const emit = defineEmits<{
  (e: "visitsChanged", skip: number, take: number): void;
  (e: "visitClicked", visitId: number): void;
  (e: "groupPlantClicked", visit: VisitDisplay): void;
  (e: "visitSelected", visit: VisitDisplay): void;
}>();

const take = ref<number>(props.defaultTake ?? 10);
const skip = ref<number>(props.skip);

const loadScrollThreshold = 100;

const scrollHandler = function (e: Event) {
  const target = e.target as HTMLDivElement | null;
  if (!target) return;
  if (
    target.scrollTop + loadScrollThreshold <
    target.scrollHeight - target.clientHeight
  )
    return;
  if (skip.value + take.value >= props.totalVisits) return;
  if (props.loading) return;

  skip.value = skip.value + take.value;
  emit("visitsChanged", skip.value, take.value);
};

const onVisitClicked = function (visitId: number) {
  emit("visitClicked", visitId);
};

const onGroupPlantClicked = function (visit: VisitDisplay) {
  emit("groupPlantClicked", visit);
};

const onVisitSelected = function (visit: VisitDisplay) {
  emit("visitSelected", visit);
};

watchEffect(() => {
  skip.value = props.skip;
});
</script>

<style lang="scss">
.visits-overview-list {
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;

  .visit-list {
    background: none;
    border: none;
    overflow: auto;

    flex: 1 1 100%;

    .k-listview-content {
      padding: 8px;
    }

    .k-listview-footer {
      border-top-width: 0;
    }

    &__tile {
      margin-bottom: 32px;
    }
  }
  .visit-loader {
    display: flex;
    justify-content: center;
    margin: 32px 0;
  }
}
</style>
